/**
 * User-facing wording for a FAILED order-header save in OrderDetailModal.
 *
 * Every header field (PO number, ship-to, ship via, salesperson, ...) is
 * saved by its own PATCH the moment the CSR leaves the field. Until kanban
 * 3677e207 a failed PATCH only reached console.error, so the field kept
 * showing the typed value while nothing was stored — on 2026-09-22 a CSR
 * "filled everything out" on a draft while the office IP was being
 * rate-limited (HTTP 503) and lost all of it. The message therefore always
 * says the value was NOT saved and what to do next.
 */

export type HeaderSaveErrorKind =
  | 'rate_limited'
  | 'session_expired'
  | 'forbidden'
  | 'rejected'
  | 'not_editable'
  | 'network'
  | 'server';

interface AxiosLikeError {
  response?: { status?: number; data?: { detail?: unknown } };
  request?: unknown;
  code?: string;
  message?: string;
}

function asAxiosLike(err: unknown): AxiosLikeError | null {
  return err && typeof err === 'object' ? (err as AxiosLikeError) : null;
}

function detailText(err: AxiosLikeError): string | null {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    // FastAPI 422 body: [{loc, msg, type}, ...]
    const msgs = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : null))
      .filter((m): m is string => !!m);
    if (msgs.length) return msgs.join('; ');
  }
  return null;
}

/** Classify a failed save so the UI can word it precisely. */
export function classifyHeaderSaveError(err: unknown): HeaderSaveErrorKind {
  const e = asAxiosLike(err);
  const status = e?.response?.status;
  if (status === 429 || status === 503) return 'rate_limited';
  if (status === 401) return 'session_expired';
  if (status === 403) return 'forbidden';
  if (status === 400 || status === 409) return 'not_editable';
  if (status === 422) return 'rejected';
  if (status !== undefined) return 'server';
  // axios sets `request` but no `response` when nothing came back.
  if (e?.request !== undefined || e?.code === 'ECONNABORTED' || e?.code === 'ERR_NETWORK') return 'network';
  return 'server';
}

/**
 * Build the alert text for a failed header save.
 *
 * @param err       whatever the service call rejected with
 * @param fieldLabel human label of what was being saved ("PO number", "ship-to address")
 */
export function formatHeaderSaveError(err: unknown, fieldLabel: string): string {
  const e = asAxiosLike(err);
  const notSaved = `${fieldLabel} was NOT saved`;
  switch (classifyHeaderSaveError(err)) {
    case 'rate_limited':
      return `${notSaved}: the server is rate-limiting requests from your office right now (HTTP ${e?.response?.status ?? 503}). Wait a few seconds, then re-enter it and check it stuck.`;
    case 'session_expired':
      return `${notSaved}: your session has expired. Sign in again, reopen this order and re-enter it.`;
    case 'forbidden':
      return `${notSaved}: your account is not allowed to edit orders.`;
    case 'not_editable': {
      const detail = e ? detailText(e) : null;
      return detail
        ? `${notSaved}: ${detail}`
        : `${notSaved}: this order can no longer be edited here (it may already be submitted). Reload it to see its current state.`;
    }
    case 'rejected': {
      const detail = e ? detailText(e) : null;
      return detail ? `${notSaved}: ${detail}` : `${notSaved}: the value was rejected. Check it and try again.`;
    }
    case 'network':
      return `${notSaved}: the server could not be reached. Check your connection, then re-enter it.`;
    case 'server':
    default: {
      const detail = e ? detailText(e) : null;
      const status = e?.response?.status;
      return detail
        ? `${notSaved}: ${detail}`
        : `${notSaved}${status ? ` (server error ${status})` : ''}. Try again; if it keeps failing, tell IT which field you were saving.`;
    }
  }
}

/**
 * Short reason phrase for a failed order write, for notices that already
 * name the thing being written ("Quote #09821 could not be attached: …").
 * Same classification and FastAPI `detail` extraction as
 * formatHeaderSaveError, without the "was NOT saved / re-enter it" framing
 * that only fits a header field.
 */
export function describeSaveFailure(err: unknown): string {
  const e = asAxiosLike(err);
  const status = e?.response?.status;
  const detail = e ? detailText(e) : null;
  switch (classifyHeaderSaveError(err)) {
    case 'rate_limited':
      return `the server is rate-limiting requests from your office right now (HTTP ${status ?? 503}). Wait a few seconds and try again.`;
    case 'session_expired':
      return 'your session has expired. Sign in again and reopen this order.';
    case 'forbidden':
      return 'your account is not allowed to edit orders.';
    case 'not_editable':
      return detail
        ? `${detail} (HTTP ${status})`
        : 'this order can no longer be edited here (it may already be submitted). Reload it to see its current state.';
    case 'rejected':
      return detail ? `${detail} (HTTP 422)` : 'the server rejected the value (HTTP 422).';
    case 'network':
      return 'the server could not be reached. Check your connection and try again.';
    case 'server':
    default:
      return detail
        ? `${detail} (HTTP ${status})`
        : `${status ? `server error ${status}` : 'unexpected error'}. Try again; if it keeps failing, tell IT.`;
  }
}
